import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarIcon, FileUp, ScanText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errText } from "@/lib/api";
import { TimeSelect } from "@/components/TimeSelect";
import { EVENT_TYPES, SESSION_FLAGS, isoDate, longDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const SAMPLE = `ITEM 1  W.P.(C) 9032/2026  ORION TEXTILES PVT LTD VS COMMISSIONER OF GST ...FOR ADMISSION  10:30 AM
ITEM 2  ARB.P. 88/2026  VERTEX INFRA PVT LTD VS NHAI ...OBJECTIONS U/S 34  AFTER LUNCH
ITEM 3  CRL.A. 512/2026  SHARMA VS STATE OF DELHI ...ARGUMENTS ON BAIL  AT 12:00 NOON`;

export default function CauseList() {
  const { canWrite, workspace } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [date, setDate] = useState(isoDate(new Date()));
  const [courtId, setCourtId] = useState(null);
  const [courts, setCourts] = useState([]);
  const [items, setItems] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const loadCourts = useCallback(async () => {
    try {
      const { data } = await api.get("/courts");
      setCourts(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [workspace?.workspace_id]);

  useEffect(() => {
    loadCourts();
  }, [loadCourts]);

  const parse = async () => {
    if (text.trim().length < 10) return toast.error("Paste the cause list text first");
    setParsing(true);
    try {
      const { data } = await api.post("/causelist/parse", { text });
      setItems(data.items);
      setSource(null);
      toast.success(`Read ${data.count} listing(s) — review before saving`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setParsing(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name))
      return toast.error("That needs to be a PDF");
    if (file.size > 15 * 1024 * 1024) return toast.error("That PDF is over 15 MB");
    setParsing(true);
    setProgress(0);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/causelist/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
        onUploadProgress: (e) =>
          setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      setItems(data.items);
      setText(data.text || "");
      setSource({ filename: data.filename, pages: data.pages, scanned: data.scanned });
      toast.success(
        `Read ${data.count} listing(s) off ${data.pages} page(s) — review before saving`,
      );
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setParsing(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const update = (idx, patch) =>
    setItems((list) => list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const commit = async () => {
    const included = items.filter((i) => i.include);
    if (!included.length) return toast.error("Select at least one listing");
    setSaving(true);
    try {
      const { data } = await api.post("/causelist/commit", {
        date,
        court_id: courtId,
        items: included.map((i) => ({
          item_no: i.item_no,
          case_number: i.case_number,
          matter_title: i.matter_title,
          parties: i.parties,
          start_time: i.start_time,
          session_flag: i.session_flag,
          event_type: i.event_type,
          notes: i.notes,
          include: true,
          matter_id: i.matter_id,
          create_matter: !i.matter_id && i.create_matter,
        })),
      });
      toast.success(
        `${data.events_created} entries added${data.matters_created ? `, ${data.matters_created} new matters` : ""}`,
      );
      setItems(null);
      setText("");
      setSource(null);
      navigate(`/calendar?view=day&date=${date}`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="causelist-page">
      <div>
        <p className="label-caps">Fast entry</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">Read a cause list</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          Drop the board text from any court, or upload the cause-list PDF straight off the court
          website. It is read into diary entries — item numbers, case numbers, times and sessions —
          and matched to your existing matters. Nothing is saved until you approve the review table.
        </p>
      </div>

      {!canWrite ? (
        <p className="surface p-6 text-sm text-muted-foreground">
          You have read-only access to this diary.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <Label className="label-caps">Cause list text</Label>
              <Textarea
                data-testid="causelist-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                placeholder={SAMPLE}
                className="rounded-sm text-base font-mono"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="causelist-parse-btn"
                  onClick={parse}
                  disabled={parsing}
                  className="rounded-full h-12 px-6"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  {parsing ? "Reading the board…" : "Read with AI"}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  data-testid="causelist-pdf-input"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                />
                <Button
                  variant="outline"
                  data-testid="causelist-pdf-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={parsing}
                  className="rounded-full h-12 px-6"
                >
                  <FileUp className="h-5 w-5 mr-2" />
                  {progress && progress < 100 ? `Sending… ${progress}%` : "Upload a PDF"}
                </Button>
                <Button
                  variant="outline"
                  data-testid="causelist-sample-btn"
                  onClick={() => setText(SAMPLE)}
                  className="rounded-full h-12"
                >
                  <ScanText className="h-5 w-5 mr-2" /> Use sample
                </Button>
              </div>
              {source ? (
                <p className="text-xs text-muted-foreground" data-testid="causelist-source">
                  Read from <span className="font-semibold">{source.filename}</span> ·{" "}
                  {source.pages} page{source.pages === 1 ? "" : "s"} ·{" "}
                  {source.scanned ? "scanned board, read as a picture" : "text read off the PDF"}
                </p>
              ) : null}
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="label-caps">Listing date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="causelist-date-trigger"
                      className="w-full justify-start rounded-sm h-11 text-base font-normal"
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" /> {longDate(date)}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(date)}
                      onSelect={(d) => d && setDate(isoDate(d))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Court</Label>
                <Select value={courtId || "none"} onValueChange={(v) => setCourtId(v === "none" ? null : v)}>
                  <SelectTrigger data-testid="causelist-court-select" className="rounded-sm h-11 text-base">
                    <SelectValue placeholder="Select court" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No court</SelectItem>
                    {courts.map((c) => (
                      <SelectItem key={c.court_id} value={c.court_id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {items ? (
            <div className="space-y-4" data-testid="causelist-review">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base md:text-lg font-heading font-semibold">
                  Review {items.length} listing(s) for {longDate(date)}
                </h2>
                <Button
                  data-testid="causelist-commit-btn"
                  onClick={commit}
                  disabled={saving}
                  className="rounded-full h-12 px-6"
                >
                  {saving ? "Adding…" : `Add ${items.filter((i) => i.include).length} to diary`}
                </Button>
              </div>

              <div className="surface overflow-x-auto">
                <table className="w-full text-left min-w-[60rem]">
                  <thead>
                    <tr className="border-b border-border">
                      {["Use", "Item", "Matter", "Case no.", "Type", "Time / session", "Matter link"].map((h) => (
                        <th key={h} className="px-4 py-3 label-caps whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx} className="border-b border-border align-top" data-testid={`causelist-row-${idx}`}>
                        <td className="px-4 py-3">
                          <Checkbox
                            data-testid={`causelist-include-${idx}`}
                            checked={it.include}
                            onCheckedChange={(v) => update(idx, { include: !!v })}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">{it.item_no || "—"}</td>
                        <td className="px-4 py-3 min-w-[16rem]">
                          <Input
                            data-testid={`causelist-title-${idx}`}
                            value={it.matter_title}
                            onChange={(e) => update(idx, { matter_title: e.target.value })}
                            className="rounded-sm h-10 text-sm"
                          />
                          {it.notes ? (
                            <p className="mt-1 text-xs text-muted-foreground">{it.notes}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            data-testid={`causelist-case-${idx}`}
                            value={it.case_number || ""}
                            onChange={(e) => update(idx, { case_number: e.target.value })}
                            className="rounded-sm h-10 text-sm w-40"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Select value={it.event_type} onValueChange={(v) => update(idx, { event_type: v })}>
                            <SelectTrigger
                              data-testid={`causelist-type-${idx}`}
                              className="rounded-sm h-10 text-sm w-44"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {EVENT_TYPES.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 w-52">
                          {it.event_type === "fixed_time" || it.event_type === "post_court" ? (
                            <TimeSelect
                              value={it.start_time}
                              onChange={(v) => update(idx, { start_time: v })}
                              testId={`causelist-time-${idx}`}
                            />
                          ) : it.event_type === "session_based" ? (
                            <Select
                              value={it.session_flag || "both"}
                              onValueChange={(v) => update(idx, { session_flag: v })}
                            >
                              <SelectTrigger
                                data-testid={`causelist-session-${idx}`}
                                className="rounded-sm h-10 text-sm"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SESSION_FLAGS.map((s) => (
                                  <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-sm text-muted-foreground">Full day</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {it.matter_id ? (
                            <span className="chip chip-session">{it.matched_matter_title}</span>
                          ) : (
                            <label className="flex items-center gap-2">
                              <Checkbox
                                data-testid={`causelist-create-matter-${idx}`}
                                checked={!!it.create_matter}
                                onCheckedChange={(v) => update(idx, { create_matter: !!v })}
                              />
                              <span className="text-xs">Create matter</span>
                            </label>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
